import React, { useState, useRef, useEffect } from "react";
import { useApp } from "@/context/AppContext";
import { API, api } from "@/lib/api";
import { Sparkles, Send, Lightbulb, Target, TrendingDown, Zap, Sun, Cpu, RefreshCw } from "lucide-react";
import { motion } from "framer-motion";

const SESSION_KEY = "solair_coach_session";
const PLANT_KEY = "solair_coach_plant";
const getSession = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) { s = crypto.randomUUID(); localStorage.setItem(SESSION_KEY, s); }
  return s;
};

const STARTERS_IT_WITH_PLANT = [
  "Quanto ho prodotto oggi e quanto ho risparmiato?",
  "Perché la mia produzione è calata questo mese?",
  "Quando conviene accendere la lavatrice oggi?",
  "Mi conviene aggiungere una batteria di accumulo?",
];

const STARTERS_IT_NO_PLANT = [
  "Voglio ridurre la bolletta della luce",
  "Ho 4 persone in casa, come organizzare i consumi",
  "Ho un'auto elettrica, quando ricaricarla?",
  "Consigli su elettrodomestici da sostituire",
];

const STARTERS_EN_WITH_PLANT = [
  "How much did I produce today and save?",
  "Why did my production drop this month?",
  "When should I run my washing machine today?",
  "Should I add a battery to my system?",
];

const STARTERS_EN_NO_PLANT = [
  "I want to lower my electricity bill",
  "4 people at home, how to organize consumption",
  "I have an EV, when should I charge it?",
  "Advice on which appliances to replace",
];

export default function Coach() {
  const { lang, user } = useApp();
  const isIT = lang === "it";
  const [plants, setPlants] = useState([]);
  const [selectedPlant, setSelectedPlant] = useState(localStorage.getItem(PLANT_KEY) || "");
  const [plantsLoaded, setPlantsLoaded] = useState(false);

  const [messages, setMessages] = useState([
    { role: "assistant", content: isIT
      ? "Ciao! Sono il tuo Energy Coach. Aiutami a capire come vivi la casa e ti darò azioni concrete per risparmiare. Da dove partiamo?"
      : "Hi! I'm your Energy Coach. Help me understand how you live at home and I'll give you concrete actions to save. Where do we start?" }
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get("/plants")
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        setPlants(list);
        if (list.length && !selectedPlant) {
          const first = list[0].id;
          setSelectedPlant(first);
          localStorage.setItem(PLANT_KEY, first);
        }
      })
      .catch(() => setPlants([]))
      .finally(() => setPlantsLoaded(true));
  }, [user]);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const currentPlant = plants.find((p) => p.id === selectedPlant);

  const send = async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    setSending(true);
    try {
      const token = localStorage.getItem("solair_token");
      const res = await fetch(`${API}/coach/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({
          session_id: getSession(),
          message: msg,
          lang,
          plant_id: selectedPlant || null,
          context: null,
        }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n"); buffer = lines.pop();
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const chunk = line.slice(6);
            if (chunk === "[DONE]") continue;
            setMessages((m) => {
              const c = [...m];
              c[c.length - 1] = { role: "assistant", content: c[c.length - 1].content + chunk };
              return c;
            });
          }
        }
      }
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "Errore connessione AI." }]);
    } finally { setSending(false); }
  };

  const resetChat = () => {
    localStorage.removeItem(SESSION_KEY);
    setMessages([{ role: "assistant", content: isIT
      ? "Nuova conversazione avviata. Di cosa vuoi parlare?"
      : "New conversation started. What do you want to talk about?" }]);
  };

  const pickPlant = (id) => {
    setSelectedPlant(id);
    localStorage.setItem(PLANT_KEY, id);
    resetChat();
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] grain-bg flex items-center justify-center px-6">
        <div className="card-surface p-12 text-center max-w-md">
          <Sparkles size={40} className="text-[#EAB308] mx-auto mb-4" />
          <h1 className="text-3xl font-black text-[#17301C] mb-3">Energy Coach</h1>
          <p className="text-[#3A4A3E] mb-6">Accedi per parlare con il tuo coach energetico personale.</p>
          <a href="/login" className="btn-primary" data-testid="coach-login-cta">Accedi</a>
        </div>
      </div>
    );
  }

  const hasPlants = plants.length > 0;
  const starters = hasPlants
    ? (isIT ? STARTERS_IT_WITH_PLANT : STARTERS_EN_WITH_PLANT)
    : (isIT ? STARTERS_IT_NO_PLANT : STARTERS_EN_NO_PLANT);

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain-bg pb-16">
      <div className="max-w-4xl mx-auto px-6 pt-12">
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 label-eco mb-4">
            <Sparkles size={12} className="text-[#EAB308]" /> Energy Coach · AI
          </div>
          <h1 className="text-5xl font-black tracking-tighter text-[#17301C] leading-[0.95]">
            {isIT ? "Il tuo" : "Your"} <span className="italic font-light text-[#EAB308]">{isIT ? "coach energetico" : "energy coach"}</span>
          </h1>
          <p className="text-lg text-[#3A4A3E] mt-4 max-w-2xl mx-auto">
            {isIT
              ? "Chiedi consigli in tempo reale sul TUO impianto: quanta energia hai prodotto oggi, quando accendere gli elettrodomestici, se ti serve una batteria."
              : "Ask real-time advice about YOUR system: how much you produced today, when to run appliances, if you need a battery."}
          </p>
        </div>

        {/* Plant selector / status banner */}
        {plantsLoaded && (
          <PlantContextBanner
            plants={plants}
            selectedPlant={selectedPlant}
            currentPlant={currentPlant}
            onPick={pickPlant}
            isIT={isIT}
          />
        )}

        <div className="card-surface overflow-hidden flex flex-col" style={{ height: "560px" }} data-testid="coach-panel">
          <div ref={listRef} className="flex-1 overflow-y-auto p-6 space-y-4 bg-[#F9F8F6]">
            {messages.map((m, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }}
                className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-[#17301C] text-white rounded-br-sm"
                    : "bg-white border border-[#DDE0D8] text-[#121A14] rounded-bl-sm"
                }`}>
                  {m.content || <span className="opacity-50">...</span>}
                </div>
              </motion.div>
            ))}
          </div>

          {messages.length <= 1 && (
            <div className="px-6 py-3 border-t border-[#DDE0D8] bg-white">
              <div className="text-[10px] uppercase tracking-widest font-bold text-[#6E7A71] mb-2">
                {isIT ? "Prova con:" : "Try:"}
              </div>
              <div className="flex flex-wrap gap-2">
                {starters.map((s, i) => (
                  <button key={i} onClick={() => send(s)} data-testid={`coach-starter-${i}`}
                    className="px-3 py-1.5 rounded-full bg-[#EAE7E0] hover:bg-[#DDE0D8] text-xs font-semibold text-[#17301C] transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="p-4 border-t border-[#DDE0D8] bg-white flex gap-2">
            <button
              data-testid="coach-reset"
              onClick={resetChat}
              title={isIT ? "Nuova conversazione" : "New conversation"}
              className="p-2.5 rounded-lg border border-[#DDE0D8] text-[#6E7A71] hover:bg-[#EAE7E0] transition-colors"
            >
              <RefreshCw size={16} />
            </button>
            <input data-testid="coach-input" value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={isIT ? "Scrivi al tuo coach..." : "Message your coach..."}
              className="flex-1 px-4 py-2.5 rounded-lg border border-[#DDE0D8] text-sm focus:outline-none focus:border-[#17301C] focus:ring-1 focus:ring-[#17301C]"
              disabled={sending} />
            <button data-testid="coach-send" onClick={() => send()} disabled={sending || !input.trim()}
              className="px-4 py-2.5 rounded-lg bg-[#EAB308] text-[#17301C] font-semibold transition-colors hover:bg-[#CA9A04] disabled:opacity-50">
              <Send size={16} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <FeatureCard icon={Target} title={isIT ? "Dati in tempo reale" : "Real-time data"}
            body={isIT ? "Il coach conosce il tuo impianto: kWp, città, batteria, produzione di oggi." : "The coach knows your system: kWp, city, battery, today's production."} />
          <FeatureCard icon={Lightbulb} title={isIT ? "Azioni concrete" : "Concrete actions"}
            body={isIT ? "3-4 azioni con stima risparmio €/anno subito applicabili." : "3-4 actions with €/year savings estimate, immediately applicable."} />
          <FeatureCard icon={TrendingDown} title={isIT ? "Meno bolletta" : "Lower bills"}
            body={isIT ? "Fino al 25% di riduzione con abitudini smart + fotovoltaico." : "Up to 25% reduction with smart habits + solar."} />
        </div>
      </div>
    </div>
  );
}

function PlantContextBanner({ plants, selectedPlant, currentPlant, onPick, isIT }) {
  if (plants.length === 0) {
    return (
      <div className="mb-6 card-surface p-4 flex items-center justify-between gap-3 border-l-4 border-[#EAB308]" data-testid="coach-no-plants">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#EAB308]/20 flex items-center justify-center flex-shrink-0">
            <Sun size={18} className="text-[#EAB308]" />
          </div>
          <div>
            <div className="font-bold text-[#17301C] text-sm">
              {isIT ? "Nessun impianto collegato" : "No plant connected"}
            </div>
            <div className="text-xs text-[#6E7A71]">
              {isIT
                ? "Aggiungi il tuo impianto dalla Dashboard per ricevere consigli su misura sui dati reali."
                : "Add your plant in the Dashboard to get advice tailored to your real data."}
            </div>
          </div>
        </div>
        <a href="/dashboard" data-testid="coach-add-plant-cta"
          className="text-xs font-black uppercase tracking-widest px-4 py-2 rounded-full bg-[#17301C] text-white hover:bg-[#0f2013] whitespace-nowrap">
          {isIT ? "Aggiungi" : "Add"}
        </a>
      </div>
    );
  }
  return (
    <div className="mb-6 card-surface p-4 border-l-4 border-[#8BA892]" data-testid="coach-plant-banner">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#8BA892]/20 flex items-center justify-center flex-shrink-0">
            <Zap size={18} className="text-[#166534]" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#6E7A71]">
              {isIT ? "Coach connesso all'impianto" : "Coach connected to plant"}
            </div>
            <div className="font-bold text-[#17301C]" data-testid="coach-plant-name">
              {currentPlant?.name || "—"}
              {currentPlant && (
                <span className="ml-2 text-xs font-mono-nums text-[#6E7A71]">
                  {currentPlant.kwp} kWp · {currentPlant.location}
                  {currentPlant.battery_kwh > 0 && <> · <Cpu size={10} className="inline" /> {currentPlant.battery_kwh} kWh</>}
                </span>
              )}
            </div>
          </div>
        </div>
        {plants.length > 1 && (
          <select
            data-testid="coach-plant-select"
            value={selectedPlant}
            onChange={(e) => onPick(e.target.value)}
            className="px-3 py-2 rounded-lg border border-[#DDE0D8] bg-white text-sm font-semibold text-[#17301C] focus:outline-none focus:border-[#17301C]"
          >
            {plants.map((p) => (
              <option key={p.id} value={p.id}>{p.name} · {p.kwp} kWp</option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
}

const FeatureCard = ({ icon: Icon, title, body }) => (
  <div className="card-surface p-5">
    <div className="w-10 h-10 rounded-lg bg-[#EAB308] flex items-center justify-center mb-3">
      <Icon size={18} className="text-[#17301C]" />
    </div>
    <div className="font-bold text-[#17301C] mb-1">{title}</div>
    <div className="text-sm text-[#3A4A3E]">{body}</div>
  </div>
);
